import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, ArrowLeft, RefreshCw } from 'lucide-react';
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

interface ErrorBoundaryProps {
  children: ReactNode;
  resetKey?: string;
  homeHref?: string;
  homeLabel?: string;
}

interface ErrorBoundaryState {
  error: unknown | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    return { error };
  }

  override componentDidUpdate(prevProps: ErrorBoundaryProps) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  override componentDidCatch(error: unknown, info: ErrorInfo) {
    console.error('Route render failed', error, info);
  }

  override render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="flex min-h-[320px] items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-2 text-center">
            <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-danger-soft text-danger">
              <AlertTriangle className="size-5" aria-hidden />
            </div>
            <CardTitle>Something broke on this page.</CardTitle>
            <CardDescription>The rest of the app is still available.</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-2 sm:flex-row sm:justify-center">
            <Button asChild variant="ghost">
              <Link to={this.props.homeHref ?? '/'}>
                <ArrowLeft className="size-4" />
                {this.props.homeLabel ?? 'Back to your workspace'}
              </Link>
            </Button>
            <Button variant="outline" onClick={() => this.setState({ error: null })}>
              <RefreshCw className="size-4" />
              Try again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
}
